import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Droplets, Loader2, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  "Drain Cleaning", "Water Heater Repair", "Leak Detection", "Pipe Repair",
  "Emergency Plumbing", "Fixture Installation", "Sewer Line", "Sump Pump",
  "Gas Lines", "Repiping", "Water Treatment", "Remodeling",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { business, user, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: business?.name || "",
    owner_name: business?.owner_name || user?.name || "",
    email: business?.email || user?.email || "",
    phone: "",
    website: "",
    address: "",
    zip_code: "",
    services: [],
    business_hours: { weekday: "8:00 AM - 6:00 PM", saturday: "9:00 AM - 2:00 PM", sunday: "Closed" },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setHours = (k) => (e) => setForm({ ...form, business_hours: { ...form.business_hours, [k]: e.target.value } });
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/business/onboarding", form);
      await refresh();
      toast.success("Workspace ready!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const canNext = step === 1 ? form.name && form.owner_name && form.email : true;

  return (
    <div className="min-h-screen bg-slate-50 grid-dots py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><Droplets className="h-5 w-5 text-white" /></div>
          <span className="font-heading font-extrabold text-xl">PlumbFlow</span>
        </div>
        <Card className="p-8 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Badge variant="secondary">Step {step} of 3</Badge>
            <span className="text-sm text-slate-500">{Math.round((step / 3) * 100)}% complete</span>
          </div>
          <Progress value={(step / 3) * 100} className="mb-6 h-2" />

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-1">
              <div>
                <h1 className="text-2xl font-bold font-heading">Tell us about your business</h1>
                <p className="text-slate-500 text-sm mt-1">This is how customers will see you.</p>
              </div>
              <div><Label>Business name *</Label><Input value={form.name} onChange={set("name")} data-testid="onboarding-name" className="mt-1" placeholder="Smith Plumbing Co." /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Owner name *</Label><Input value={form.owner_name} onChange={set("owner_name")} data-testid="onboarding-owner" className="mt-1" /></div>
                <div><Label>Business email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="onboarding-email" className="mt-1" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="onboarding-phone" className="mt-1" placeholder="(555) 123-4567" /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={set("website")} data-testid="onboarding-website" className="mt-1" placeholder="https://" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-2">
              <div>
                <h1 className="text-2xl font-bold font-heading">Service area & address</h1>
                <p className="text-slate-500 text-sm mt-1">Where do you operate?</p>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="onboarding-address" className="mt-1" placeholder="123 Main St, City, State" /></div>
              <div><Label>ZIP code</Label><Input value={form.zip_code} onChange={set("zip_code")} data-testid="onboarding-zip" className="mt-1 max-w-[160px]" placeholder="94105" /></div>
              <div>
                <Label className="mb-2 block">Services offered</Label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => toggleService(s)} data-testid={`onboarding-service-${s.split(" ")[0].toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.services.includes(s) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>
                      {form.services.includes(s) && <Check className="h-3.5 w-3.5 inline mr-1" />}{s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-3">
              <div>
                <h1 className="text-2xl font-bold font-heading">Business hours</h1>
                <p className="text-slate-500 text-sm mt-1">When are you available?</p>
              </div>
              <div><Label>Monday – Friday</Label><Input value={form.business_hours.weekday} onChange={setHours("weekday")} data-testid="onboarding-hours-weekday" className="mt-1" /></div>
              <div><Label>Saturday</Label><Input value={form.business_hours.saturday} onChange={setHours("saturday")} data-testid="onboarding-hours-saturday" className="mt-1" /></div>
              <div><Label>Sunday</Label><Input value={form.business_hours.sunday} onChange={setHours("sunday")} data-testid="onboarding-hours-sunday" className="mt-1" /></div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onboarding-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            ) : <span />}
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="onboarding-next" className="bg-blue-600 hover:bg-blue-700">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button onClick={finish} disabled={loading} data-testid="onboarding-finish" className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish setup <Check className="h-4 w-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
